import React, { useEffect, useState, useContext, FormEvent } from "react";
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { DraggableModalDialog } from './DraggableModalDialog';
import { useHttpUtils } from '../Utilities/useHttpUtils';
import useAlert from '../Alerts/useAlert';
import { LotDetailContext } from "../Contexts/LotDetailContext";

export interface SelectPackageTypeDialogProps {
    showDialog: boolean,
    closeDialog: () => void
}

export interface ChangePackageTypeDTO {
    materialCode: string,
    comment?: string,
}

export default function SelectPackageTypeDialog({ showDialog, closeDialog }: SelectPackageTypeDialogProps) {
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { t } = useTranslation();
    const { lotDetail: { summary: { lotNumber, grade, materialCode: currentMaterialCode } }, getLotDetail } = useContext(LotDetailContext);

    const [packageTypes, setPackageTypes] = useState<any[]>([]);
    const [materialCode, setMaterialCode] = useState(currentMaterialCode);
    const [loading, setLoading] = useState(false);
    const [formValidated, setFormValidated] = useState(false);

    useEffect(() => {
        setMaterialCode(currentMaterialCode);
    }, [currentMaterialCode]);

    useEffect(() => {
        const getPackageTypes = () => {
            setLoading(true);
            fetchDataAuthenticated(`/api/v1/recipeToPackageType/?recipeName=${grade}`)
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    }
                    throw new Error('Failed to get package types');
                })
                .then(data => {
                    setPackageTypes(data);
                })
                .catch(error => {
                    setAlert("danger", "Error", error.message);
                })
                .finally(() => {
                    setLoading(false);
                });
        };
        if (showDialog) {
            getPackageTypes();
        }
    }, [fetchDataAuthenticated, grade, setAlert, showDialog]);

    const handleSave = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const form = e.currentTarget;

        if (form.checkValidity() === true) {
            updatePackageType()
        } else {
            console.warn("Change lot grade form was invalid");
        }

        setFormValidated(true);
    };

    const updatePackageType = async () => {
        let url = `/api/v1/lot/${lotNumber}/packageType`;
        let body: ChangePackageTypeDTO = {
            materialCode: materialCode,
        };

        setLoading(true);
        await fetchDataAuthenticated(url, 'PUT', JSON.stringify(body))
            .then(async response => {
                if (response.ok) {
                    setAlert("success", "Success", `Updated the package type to ${materialCode}`);
                    getLotDetail();
                } else {
                    let errorMessage = await response.text();
                    console.log("Failed to update the package type: " + errorMessage);
                    throw new Error("Failed to update the package type." + errorMessage);
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const closeDialogAndResetForm = () => {
        setMaterialCode(currentMaterialCode);
        setFormValidated(false);
        closeDialog();
    };

    const formId = "selectPackageTypeForm";
    const form = (
        <Form id={formId} noValidate validated={formValidated} onSubmit={handleSave}>
            <Form.Group as={Row} className="mb-2">
                <Form.Label column>{t<string>("Dialogs.PackageType.SelectPackageType")}</Form.Label>
                <Col sm={8}>
                    <Form.Select required value={materialCode} onChange={(e) => setMaterialCode(e.target.value)}>
                        <option value="">{t<string>("Application.Controls.DefaultSelect")}</option>
                        {packageTypes &&
                            packageTypes.map((p) => {
                                return <option
                                    key={`package-type-option-${p.packageTypeName}`}
                                    value={p.materialCode}>
                                    {`${p.packageTypeName}${p.alternativeName ? ` (${p.alternativeName})` : ''}`}
                                </option>;
                            })
                        }
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{t<string>("Validation.ValueMustBeSelected")}</Form.Control.Feedback>
                </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-2">
                <Form.Label column>{t<string>("Dialogs.PackageType.SelectMaterialCode")}</Form.Label>
                <Col sm={8}>
                    <Form.Select required value={materialCode} onChange={(e) => setMaterialCode(e.target.value)}>
                        <option value="">{t<string>("Application.Controls.DefaultSelect")}</option>
                        {packageTypes &&
                            packageTypes.map((p) => {
                                return <option
                                    key={`package-type-option-${p.materialCode}`}
                                    value={p.materialCode}>
                                    {p.materialCode}
                                </option>;
                            })
                        }
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{t<string>("Validation.ValueMustBeSelected")}</Form.Control.Feedback>
                </Col>
            </Form.Group>
        </Form>
    );

    return (
        <>
            <Modal dialogAs={DraggableModalDialog} show={showDialog} onHide={closeDialogAndResetForm} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{t<string>("Dialogs.PackageType.MaterialCodeTitle")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {
                        loading
                            ?
                            <div className="d-flex justify-content-center mb-2">
                                <Spinner animation="border" />
                            </div>
                            :
                            form
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeDialogAndResetForm} disabled={loading}>
                        {t<string>("Application.Controls.Cancel")}
                    </Button>
                    <Button variant="primary" type="submit" form={formId} disabled={loading || (materialCode === currentMaterialCode)}>
                        {t<string>("Application.Controls.Save")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}